import { fetchAudienceData } from 'helpers/mparticle';
import mParticleAudienceDataMock from '../../__mocks__/mParticleAudienceData.mock';
import { CountryGroup } from '../internationalisation/classes/countryGroup';
import { countryGroupMatches, getParticipationFromQueryString, isWithinSchedule, randomNumber, } from './helpers';
import { getMvtId } from './mvt';
import { getSessionParticipations, setSessionParticipations, } from './sessionStorage';
/**
 * Generic function to get A/B test participations for any page type.
 *
 * If a fallback is passed in then it will always return a variant, regardless of which page the user is on.
 * This is because we sometimes need these settings on other pages as well.
 *
 * If the user is on the target page, or session storage contains a participation,
 * then it will also return the participations data for tracking.
 * Otherwise we assume the user has not arrived via the target page, and the participations
 * object will be empty because we do not need to track it.
 *
 * For tests with `mParticleAudience`, the user must be a member of that audience
 * (verified via the analytics profile) for the test to be eligible. If they are
 * not, another eligible test may be selected, or the fallback variant is returned
 * if no eligible test remains. Similarly, if a selected variant requires mParticle
 * attributes, the user's analytics profile must have them; otherwise, another
 * eligible test may be selected, or the fallback variant is returned if no
 * eligible test remains.
 * URL-forced participations bypass this check.
 */
export async function getPageParticipations(config, fallback) {
    const countryGroupId = CountryGroup.detect();
    const path = window.location.pathname;
    const mvtId = getMvtId();
    const queryString = window.location.search;
    const { tests, pageRegex, forceParamName, sessionStorageKey, getVariantName, } = config;
    const isTargetPage = (path) => !!path && !!path.match(pageRegex);
    const getVariant = (participations, testList, bypassScheduler = false) => {
        for (const test of testList) {
            const variantName = participations[test.name];
            if (variantName) {
                if (!bypassScheduler && !isWithinSchedule(test.scheduler)) {
                    return undefined;
                }
                const variant = test.variants.find((v) => getVariantName(v) === variantName);
                if (variant) {
                    return variant;
                }
            }
        }
        return undefined;
    };
    // Fetched at most once per call, shared by mParticle eligibility checks.
    let fetchedUserAttributes;
    let audienceDataPromise = null;
    const getAudienceData = () => {
        if (!audienceDataPromise) {
            audienceDataPromise = fetchAudienceData().then((data) => {
                fetchedUserAttributes = data.userAttributes;
                return data;
            });
        }
        return audienceDataPromise;
    };
    const isUserInAudience = async (test) => {
        if (test.mParticleAudience === undefined) {
            return true;
        }
        const { audienceMemberships } = await getAudienceData();
        return audienceMemberships.includes(test.mParticleAudience);
    };
    const hasRequiredMParticleTestAttributes = async (test) => {
        const requiredAttributes = test.mParticleTemplates ?? [];
        if (requiredAttributes.length === 0) {
            return true;
        }
        const { userAttributes } = await getAudienceData();
        return requiredAttributes.every((attribute) => {
            const value = userAttributes[attribute];
            return typeof value === 'string' || typeof value === 'number';
        });
    };
    // Only track participation if user is on the target page
    const trackParticipation = isTargetPage(path);
    const makeFallbackResult = () => {
        if (!fallback) {
            return { participations: {}, variant: undefined };
        }
        const variant = fallback.variant(countryGroupId);
        return {
            participations: trackParticipation
                ? { [fallback.participationKey]: getVariantName(variant) }
                : {},
            variant,
        };
    };
    const previewParamName = forceParamName.replace('force-', 'preview-');
    // Is the participation forced in the url querystring? (bypass audience check)
    const urlParticipations = getParticipationFromQueryString(queryString, forceParamName);
    if (urlParticipations) {
        const selectedTest = tests.find((test) => urlParticipations[test.name]);
        const variant = getVariant(urlParticipations, tests);
        if (!variant || !selectedTest) {
            return makeFallbackResult();
        }
        // Forced participations bypass the audience check but still validate test attributes.
        if (!(await hasRequiredMParticleTestAttributes(selectedTest))) {
            return makeFallbackResult();
        }
        setSessionParticipations(urlParticipations, sessionStorageKey);
        return {
            participations: trackParticipation
                ? urlParticipations
                : {},
            variant,
            userAttributes: fetchedUserAttributes,
        };
    }
    // Is the participation requested via preview param? (bypass scheduler + audience check)
    const previewParticipations = getParticipationFromQueryString(queryString, previewParamName);
    if (previewParticipations) {
        const selectedTest = tests.find((test) => previewParticipations[test.name]);
        const variant = getVariant(previewParticipations, tests, true);
        if (!variant || !selectedTest) {
            return makeFallbackResult();
        }
        setSessionParticipations(previewParticipations, sessionStorageKey);
        return {
            participations: trackParticipation
                ? previewParticipations
                : {},
            variant,
            // Preview mode uses mocked mParticle attributes instead of making a request.
            userAttributes: mParticleAudienceDataMock.userAttributes,
        };
    }
    // Is there already a participation in session storage?
    const sessionParticipations = getSessionParticipations(sessionStorageKey);
    if (sessionParticipations &&
        Object.entries(sessionParticipations).length > 0) {
        // Validate and prune session participations: drop entries whose key
        // does not match any current test name, or whose variant name does not
        // exist in that test's variants.
        const validParticipations = {};
        for (const [key, value] of Object.entries(sessionParticipations)) {
            const matchingTest = tests.find((test) => key === test.name);
            if (matchingTest?.variants.some((v) => getVariantName(v) === value)) {
                validParticipations[key] = value;
            }
        }
        // If nothing valid remains, continue to re-selection
        if (Object.entries(validParticipations).length > 0) {
            const selectedTest = tests.find((test) => validParticipations[test.name]);
            const variant = getVariant(validParticipations, tests);
            if (!variant ||
                !selectedTest ||
                !(await hasRequiredMParticleTestAttributes(selectedTest))) {
                return makeFallbackResult();
            }
            return {
                participations: validParticipations,
                variant,
                userAttributes: fetchedUserAttributes,
            };
        }
    }
    // No participation in session storage, assign user to a test + variant
    for (const currentTest of tests.filter((test) => test.status === 'Live')) {
        if (isWithinSchedule(currentTest.scheduler) &&
            countryGroupMatches(currentTest.regionTargeting?.targetedCountryGroups, countryGroupId) &&
            (await isUserInAudience(currentTest)) &&
            (await hasRequiredMParticleTestAttributes(currentTest))) {
            const selectionResult = config.selectVariant
                ? config.selectVariant(currentTest, mvtId)
                : undefined;
            const variant = selectionResult ??
                currentTest.variants[randomNumber(mvtId, currentTest.name) % currentTest.variants.length];
            if (variant) {
                const participations = {
                    [currentTest.name]: getVariantName(variant),
                };
                // Record the participation in session storage so that we can track it from other pages
                setSessionParticipations(participations, sessionStorageKey);
                return {
                    participations: trackParticipation
                        ? participations
                        : {},
                    variant,
                    userAttributes: fetchedUserAttributes,
                };
            }
        }
    }
    return makeFallbackResult();
}
/**
 * Wraps getPageParticipations with a fallback variant guarantee.
 * If no variant is assigned, the fallbackVariant is used instead.
 */
export async function getPageParticipationsWithFallback(config, fallbackVariant, fallbackParticipationKey) {
    return getPageParticipations(config, {
        variant: fallbackVariant,
        participationKey: fallbackParticipationKey,
    });
}
